export interface EscapeKeyHandlerOptions {
	/**
	 * If false, the handler is ignored (use this to scope behavior to "popup is open").
	 */
	readonly isActive: () => boolean;
	readonly onEscape: () => void;
	/**
	 * Higher priority wins when multiple handlers are active.
	 */
	readonly priority?: number;
}

interface EscapeKeyHandlerEntry {
	readonly id: symbol;
	readonly isActive: () => boolean;
	readonly onEscape: () => void;
	readonly priority: number;
}

const entries: EscapeKeyHandlerEntry[] = [];
let isAttached = false;

function onKeydown(event: KeyboardEvent): void {
	if (event.key !== 'Escape') return;

	let best: EscapeKeyHandlerEntry | null = null;
	for (const entry of entries) {
		if (!entry.isActive()) continue;
		if (!best || entry.priority > best.priority) {
			best = entry;
		}
	}

	if (!best) return;

	// Ensure only ONE popup closes per key press.
	event.preventDefault();
	event.stopPropagation();
	event.stopImmediatePropagation();

	best.onEscape();
}

function ensureAttached(): void {
	if (isAttached) return;
	window.addEventListener('keydown', onKeydown, { capture: true });
	isAttached = true;
}

function ensureDetachedIfEmpty(): void {
	if (!isAttached) return;
	if (entries.length > 0) return;
	window.removeEventListener('keydown', onKeydown, { capture: true });
	isAttached = false;
}

export function registerEscapeKeyHandler(options: EscapeKeyHandlerOptions): () => void {
	const entry: EscapeKeyHandlerEntry = {
		id: Symbol('escape-key-handler'),
		isActive: options.isActive,
		onEscape: options.onEscape,
		priority: options.priority ?? 0
	};

	entries.push(entry);
	ensureAttached();

	return () => {
		const idx = entries.findIndex((x) => x.id === entry.id);
		if (idx >= 0) {
			entries.splice(idx, 1);
		}
		ensureDetachedIfEmpty();
	};
}





